<template>
  <table-wrapper>
    <v-data-table-server
      v-model="selectedItems"
      class="coronet-table--highlightable"
      :items="items"
      :loading="loading"
      fixed-header
      :items-length="totalItems"
      select-strategy="page"
      show-select
      item-key="id"
      :items-per-page="pagination.pageSize"
      :no-data-text="$t('general.noDataAvailable')"
      return-object
      @update:options="
        $emit('page-changed', {
          pageSize: $event.itemsPerPage,
          page: $event.page - 1,
        })
      "
    >
      <template #headers="{ allSelected, selectAll, someSelected }">
        <tr>
          <th class="checkbox-col">
            <v-checkbox-btn
              :indeterminate="someSelected && !allSelected"
              :model-value="allSelected"
              color="primary"
              @update:model-value="selectAll(!allSelected)"
            ></v-checkbox-btn>
          </th>
          <template v-if="selectedItems.length === 0">
            <th>
              {{ $t("network.secureWebGateway.customDomainDns.table.domain") }}
            </th>
            <th>
              {{ $t("network.secureWebGateway.customDomainDns.table.ip") }}
            </th>
            <th>
              {{ $t("network.secureWebGateway.customDomainDns.table.dateAdded") }}
            </th>
            <th />
          </template>
          <template v-else>
            <th :colspan="5">
              <span>{{ selectedItems.length }} {{ $t("general.selected") }}</span>
              <v-menu v-if="!!selectedItems.length" offset-y bottom right>
                <template #activator="{ props }">
                  <v-btn
                    data-testid="bulk-actions-button"
                    class="ml-8"
                    rounded
                    color="primary"
                    v-bind="props"
                  >
                    {{ $t("general.actions") }}
                    <v-icon icon="$triangle" />
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item
                    v-for="action in bulkActions"
                    :key="action"
                    @click="onBulkActionClick(action)"
                  >
                    <v-list-item-title>
                      {{ $t(`network.secureWebGateway.customDomainDns.actions.${action}`) }}
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </th>
          </template>
        </tr>
      </template>
      <template #item="{ item, index, internalItem, isSelected, toggleSelect }">
        <tr
          :key="index"
          :data-testid="`table-row-${index}`"
          :class="{
            'coro-table-row--selected': isSelected([internalItem]),
          }"
        >
          <td class="checkbox-col">
            <v-checkbox-btn
              :model-value="isSelected(internalItem)"
              color="primary"
              @update:model-value="toggleSelect(internalItem)"
            ></v-checkbox-btn>
          </td>
          <td class="w-30" :data-testid="`custom-dns-table-name-col-row-${index}`">
            <v-icon size="40" class="mr-4" icon="$globeLocation" color="primary" />
            <span>{{ item.domain }}</span>
          </td>
          <td class="w-30" :data-testid="`custom-dns-table-ip-col-row-${index}`">
            {{ item.ip }}
          </td>
          <td class="w-30" :data-testid="`custom-dns-table-date-col-row-${index}`">
            <span class="body2">{{ getFormattedDateTime(item.createdAt) }}</span>
          </td>
          <td class="text-right coro-table-col--sticky-right">
            <v-icon
              :disabled="loading"
              class="cursor-pointer"
              icon="$trashOutlined"
              @click="onActionClick(CustomDomainDnsAction.REMOVE, item)"
            />
          </td>
        </tr>
      </template>
    </v-data-table-server>
  </table-wrapper>
</template>

<script lang="ts">
import { defineComponent, type PropType, ref, watch } from "vue";
import type { Pagination } from "@/types.ts";
import TableWrapper from "@/components/TableWrapper.vue";
import { getFormattedDateTime } from "@/_helpers/date";
import { CustomDomainDnsAction, TrustedNetworkAction } from "@/constants/network.ts";
import type { CustomDomainDnsItem } from "@/_store/network/swg/custom-domain-dns.module.ts";

export default defineComponent({
  components: { TableWrapper },
  props: {
    items: {
      type: Array as PropType<CustomDomainDnsItem[]>,
      required: true,
    },
    totalItems: {
      type: Number,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    pagination: {
      type: Object as PropType<Pagination>,
      default: () => {
        return {
          page: 0,
          pageSize: 25,
        };
      },
    },
    filtersUpdateTime: {
      type: Number,
      required: true,
    },
  },
  emits: ["action-applied", "page-changed"],
  setup(props, { emit }) {
    const selectedItems = ref<CustomDomainDnsItem[]>([]);

    const onBulkActionClick = (action: CustomDomainDnsAction) => {
      emit("action-applied", {
        action,
        selectedItems: selectedItems.value,
      });
    };

    const onActionClick = (action: CustomDomainDnsAction, item: CustomDomainDnsItem) => {
      emit("action-applied", {
        action,
        selectedItems: [item],
      });
    };

    watch(
      () => props.filtersUpdateTime,
      () => {
        selectedItems.value = [];
      }
    );

    return {
      selectedItems,
      getFormattedDateTime,
      onBulkActionClick,
      onActionClick,
      bulkActions: [CustomDomainDnsAction.REMOVE],
      itemActions: [TrustedNetworkAction.EDIT, TrustedNetworkAction.DELETE],
      CustomDomainDnsAction,
    };
  },
});
</script>

<style scoped lang="scss">
.v-table {
  border: 1px solid rgb(var(--v-theme-indigo-faint)) !important;
  box-shadow: none !important;
}
</style>
